/* eslint-disable max-lines-per-function */
import {
  Calendar,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@e2n/ui';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateInputGroupInput } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/DateInputGroupInput';

function formatDate(date: Date | undefined) {
  if (!date) {
    return '';
  }

  return dayjs(date).format('DD.MM.YYYY');
}

export function DatePickerInputGroup({
  defaultDate,
  onSelect,
  label,
  disabled = false,
}: {
  defaultDate?: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  label: string;
  disabled?: boolean;
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(defaultDate);
  const [inputValue, setInputValue] = useState(formatDate(date));

  return (
    <InputGroup className="h-fit border-neutrals-400">
      <DateInputGroupInput
        id={label}
        value={inputValue}
        disabled={disabled}
        onChange={(e) => {
          const nextValue = e.target.value;
          setInputValue(nextValue);

          if (nextValue.trim() === '') {
            setDate(undefined);
            onSelect(undefined);
            return;
          }

          const parsed = dayjs(nextValue, 'DD.MM.YYYY', true);
          if (parsed.isValid()) {
            const date = parsed.toDate();
            setDate(date);
            onSelect(date);
          } else {
            setDate(undefined);
            onSelect(undefined);
          }
        }}
      />
      <InputGroupAddon align="inline-end">
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger className="hidden md:flex" asChild>
            <Trigger disabled={disabled} />
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0 overflow-hidden"
            align="end"
            alignOffset={-8}
            sideOffset={10}>
            <Calendar
              mode="single"
              selected={date}
              month={date}
              onMonthChange={setDate}
              onSelect={(date) => {
                if (!date) {
                  return;
                }
                setDate(date);
                setInputValue(formatDate(date));
                onSelect(date);
                setIsPopoverOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerTrigger className="flex md:hidden" asChild>
            <Trigger disabled={disabled} />
          </DrawerTrigger>
          <DrawerContent className="flex flex-col gap-4 p-4">
            <div className="font-semibold text-center">{label}</div>
            <Calendar
              mode="single"
              selected={date}
              month={date}
              onMonthChange={setDate}
              onSelect={(date) => {
                if (!date) {
                  return;
                }
                setDate(date);
                setInputValue(formatDate(date));
                setIsDrawerOpen(false);
                onSelect(date);
              }}
              classNames={{
                day_today: 'bg-transparent',
                cell: 'w-full',
                head_cell:
                  'text-slate-500 rounded-md w-9 font-normal text-[0.8rem] dark:text-slate-400 w-full',
              }}
            />
          </DrawerContent>
        </Drawer>
      </InputGroupAddon>
    </InputGroup>
  );
}

function Trigger({
  disabled,
  ...props
}: {
  selectedDate?: Date | undefined;
  disabled: boolean;
} & HTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();

  return (
    <InputGroupButton
      {...props}
      disabled={disabled}
      id="date-picker"
      variant="ghost"
      size="icon-xs"
      aria-label={t('general.date.datepicker.placeholder')}>
      <FontAwesomeIcon icon={faCalendar} />
    </InputGroupButton>
  );
}
